/**
 * Lot 10 — Répartition plaque / pays.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analyticsApi, ApiRequestError, type PlaqueDistributionDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

export function PlaquePage() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<PlaqueDistributionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void analyticsApi
      .plaque(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const fr = i18n.language.toLowerCase().startsWith("fr");

  return (
    <div className="sm-page">
      <PageHeader title={t("plaque_view")} subtitle={t("plaque_view_sub")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? <p className="sm-muted">{t("loading")}</p> : null}

      {!loading && data ? (
        <>
          <p className="sm-muted">
            {t("target_threshold")}: <strong>{data.target}</strong>
            {data.countriesVisible ? null : (
              <>
                {" · "}
                {t("plaque_manager_hint", { min: data.minCohort })}
              </>
            )}
          </p>
          <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
            <h2 style={{ marginTop: 0, fontSize: 16 }}>{t("experts_by_plaque")}</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.plaques}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="plaque" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="expertCount" name={t("experts")} fill="#1F4E79" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="sm-card" style={{ marginBottom: 16 }}>
            <div className="sm-card-pad" style={{ paddingBottom: 0 }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>{t("by_plaque")}</h2>
            </div>
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>{t("plaque")}</th>
                    <th>{t("experts")}</th>
                    <th>{t("avg_score")}</th>
                    <th>{t("pct_at_threshold")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plaques.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="sm-muted" style={{ textAlign: "center" }}>
                        {t("no_data")}
                      </td>
                    </tr>
                  ) : (
                    data.plaques.map((p) => (
                      <tr key={p.plaque}>
                        <td>{p.plaque}</td>
                        <td style={{ textAlign: "center" }}>{p.expertCount}</td>
                        <td style={{ textAlign: "center" }}>
                          {p.avgScore == null ? "—" : p.avgScore}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {p.pctAtOrAbove == null ? "—" : `${p.pctAtOrAbove}%`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {data.countriesVisible ? (
            <div className="sm-card">
              <div className="sm-card-pad" style={{ paddingBottom: 0 }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>{t("by_country")}</h2>
              </div>
              <div className="sm-table-wrap">
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>{t("plaque")}</th>
                      <th>{t("country")}</th>
                      <th>{t("experts")}</th>
                      <th>{t("avg_score")}</th>
                      <th>{t("pct_at_threshold")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.countries.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="sm-muted" style={{ textAlign: "center" }}>
                          {t("no_data")}
                        </td>
                      </tr>
                    ) : (
                      data.countries.map((c) => (
                        <tr key={`${c.plaque}-${c.countryCode}`}>
                          <td>{c.plaque}</td>
                          <td>
                            {c.countryCode} —{" "}
                            {fr ? c.countryLabelFr : c.countryLabelEn}
                          </td>
                          <td style={{ textAlign: "center" }}>{c.expertCount}</td>
                          <td style={{ textAlign: "center" }}>{c.avgScore}</td>
                          <td style={{ textAlign: "center" }}>{c.pctAtOrAbove}%</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="sm-muted">{t("plaque_countries_admin_only")}</p>
          )}
        </>
      ) : null}
    </div>
  );
}
